import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useSeo } from "@/hooks/useSeo";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { categories } from "@/mocks/news";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dah79f3oh653ivfvnn2g";

const contactJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "联系我们 - 沸点爆料",
    inLanguage: "zh-CN",
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "沸点爆料",
    email: "tip@feidian-kuaixun.example",
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "爆料线索与商务合作",
      email: "tip@feidian-kuaixun.example",
      availableLanguage: ["zh-CN"],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页" },
      { "@type": "ListItem", position: 2, name: "联系我们" },
    ],
  },
];

const submitNotes: string[] = [
  "尽量写清事件发生的时间、地点与可核实的线索来源。",
  "图片、截图等材料可在表单中说明，我们会通过你留下的邮箱进一步联系。",
  "涉及个人隐私、未成年人或正在处理中的纠纷，我们会做脱敏处理后再判断是否发布。",
  "请勿提交未经他人同意的私密内容，也请避免传播无法核实的传言。",
];

export default function ContactPage() {
  const [detailLength, setDetailLength] = useState(0);
  const { status, message, submit } = useFormSubmit({
    submitAddr: SUBMIT_ADDR,
    honeypotName: "company_alt",
    successMessage: "投递成功！如果线索被采纳，我们会通过邮箱与你进一步联系。",
  });

  useSeo({
    title: "联系我们_爆料投递与商务合作 - 沸点爆料",
    description:
      "沸点爆料联系页面，提供爆料投递与商务合作入口。线索接收全天 24 小时开放，尽量写清时间、地点与可核实的线索，涉及隐私的内容我们会做脱敏处理。",
    keywords: "联系我们,爆料投递,热点爆料,商务合作,线索提交",
    path: "/contact",
    ogType: "website",
    jsonLd: contactJsonLd,
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />

      <main className="flex-1 w-full">
        <section className="w-full bg-background-100 border-b border-background-200">
          <div className="w-full px-4 md:px-6 py-8 md:py-12">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "联系我们" }]} />
            <div className="mt-5 max-w-3xl">
              <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground-950 tracking-tight">联系我们</h1>
              <p className="mt-4 text-base text-foreground-600 leading-relaxed">
                手里有第一手线索，或者想和我们聊聊内容与商务合作？填写下方表单即可，线索接收全天 24 小时开放。我们会在核实后尽快跟进，重要线索通常一个工作日内回复。
              </p>
            </div>
          </div>
        </section>

        <div className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
            {/* 表单 */}
            <div className="lg:col-span-7" id="submit">
              <div className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-8">
                <h2 className="font-heading text-xl md:text-2xl font-black text-foreground-950 flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                    <i className="ri-megaphone-line"></i>
                  </span>
                  爆料投递 / 商务联系
                </h2>
                <p className="mt-2 text-sm text-foreground-600">带 * 的为必填项，信息仅用于线索核实与回复。</p>

                <form id="contact-form" data-readdy-form onSubmit={submit} className="mt-6 flex flex-col gap-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="contact-nickname" className="text-sm text-foreground-700">
                        你的称呼 <span className="text-primary-600">*</span>
                      </label>
                      <input
                        id="contact-nickname"
                        name="nickname"
                        type="text"
                        required
                        placeholder="可以是昵称"
                        className="w-full h-11 px-4 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="contact-email" className="text-sm text-foreground-700">
                        联系邮箱 <span className="text-primary-600">*</span>
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        placeholder="you@example.com"
                        autoComplete="email"
                        className="w-full h-11 px-4 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="contact-type" className="text-sm text-foreground-700">
                      线索类型 <span className="text-primary-600">*</span>
                    </label>
                    <select
                      id="contact-type"
                      name="category"
                      required
                      defaultValue=""
                      className="w-full h-11 px-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none cursor-pointer"
                    >
                      <option value="" disabled>
                        请选择线索类型
                      </option>
                      {categories.map((cat) => (
                        <option key={cat.slug} value={cat.name}>
                          {cat.name}
                        </option>
                      ))}
                      <option value="商务合作">商务合作</option>
                      <option value="其他问题">其他问题</option>
                    </select>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center justify-between">
                      <label htmlFor="contact-detail" className="text-sm text-foreground-700">
                        事件经过 / 合作需求 <span className="text-primary-600">*</span>
                      </label>
                      <span className={`text-xs ${detailLength > 480 ? "text-primary-600" : "text-foreground-500"}`}>
                        {detailLength}/500
                      </span>
                    </div>
                    <textarea
                      id="contact-detail"
                      name="detail"
                      required
                      maxLength={500}
                      rows={6}
                      onChange={(e) => setDetailLength(e.target.value.length)}
                      placeholder="请尽量写清时间、地点、涉及方与可核实的线索，不超过 500 字。"
                      className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none resize-none leading-relaxed"
                    ></textarea>
                  </div>

                  <input
                    type="text"
                    name="company_alt"
                    className="contact-note-extra"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed w-full sm:w-fit whitespace-nowrap"
                  >
                    {status === "submitting" ? "提交中…" : "提交线索"}
                  </button>

                  {message && (
                    <p
                      role="status"
                      className={`text-sm leading-relaxed rounded-md px-4 py-3 ${
                        status === "success"
                          ? "bg-accent-100 text-accent-900"
                          : "bg-primary-100 text-primary-800"
                      }`}
                    >
                      {message}
                    </p>
                  )}
                </form>
              </div>
            </div>

            {/* 侧栏 */}
            <aside className="lg:col-span-5 flex flex-col gap-6">
              <div className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-6">
                <h2 className="font-heading font-bold text-lg text-foreground-950">直接联系我们</h2>
                <ul className="mt-4 flex flex-col gap-4 text-sm">
                  <li className="flex items-start gap-3">
                    <span className="shrink-0 w-9 h-9 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 text-lg">
                      <i className="ri-mail-line"></i>
                    </span>
                    <span>
                      <span className="block text-foreground-900">线索与合作邮箱</span>
                      <a href="mailto:tip@feidian-kuaixun.example" className="text-foreground-600 hover:text-primary-600 transition-colors">
                        tip@feidian-kuaixun.example
                      </a>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="shrink-0 w-9 h-9 flex items-center justify-center rounded-md bg-accent-100 text-accent-800 text-lg">
                      <i className="ri-time-line"></i>
                    </span>
                    <span>
                      <span className="block text-foreground-900">线索接收时间</span>
                      <span className="text-foreground-600">全天 24 小时，重要线索一个工作日内回复</span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="shrink-0 w-9 h-9 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-800 text-lg">
                      <i className="ri-shield-check-line"></i>
                    </span>
                    <span>
                      <span className="block text-foreground-900">隐私保护</span>
                      <span className="text-foreground-600">涉及隐私的信息将做脱敏处理，未经同意不会公开你的身份</span>
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-background-100 border border-background-200 rounded-lg p-5 md:p-6">
                <h2 className="font-heading font-bold text-lg text-foreground-950">投稿须知</h2>
                <ul className="mt-4 flex flex-col gap-3">
                  {submitNotes.map((note) => (
                    <li key={note} className="flex items-start gap-2.5 text-sm text-foreground-600 leading-relaxed">
                      <span className="w-4 h-4 flex items-center justify-center text-primary-600 mt-0.5 shrink-0">
                        <i className="ri-checkbox-circle-line"></i>
                      </span>
                      {note}
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
      <BackToTop />
    </div>
  );
}